import { Box, Typography } from "@mui/material";

import { MediaFullScreen } from "../../../common/components/media/MediaFullScreen";
import TableCellTruncated from "../../../common/components/table/TableCellTruncated";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { formatTimestamp } from "../../../common/utils/timestampUtils";
import FeedbackDetailsTableCellContainer from "../components/feedback/FeedbackDetailsTableCellContainer";
import FeedbackThumbsTableCellContainer from "../components/feedback/FeedbackThumbsTableCellContainer";
import { EventTableColumn } from "../components/table/EventTable";
import { EventViewModel } from "../models/eventViewModel";
import { RecordingViewModel } from "../models/recordingViewModel";
import { useUpdateEventFeedback } from "./events/useUpdateEventFeedback";

export const useBasicEventTableColumns = (
  newestRecording?: RecordingViewModel
) => {
  const { updateEventFeedback } = useUpdateEventFeedback();

  const { getCachedLocationName } = useLocationFromCache();

  const columns: EventTableColumn<EventViewModel>[] = [
    {
      title: "Event",
      width: "22%",
      content: event => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.75em",
            cursor: "pointer",
            position: "relative",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Box sx={{ width: 70, height: 40 }}>
              <MediaFullScreen
                keyFrame={newestRecording?.keyFrame}
                mediaOutput={newestRecording?.mediaOutput}
                s3ImageFullScreenProps={{
                  loadingFallback: (
                    <Box
                      sx={{
                        backgroundColor: theme =>
                          theme.palette.otherOutlineBorder.main,
                      }}
                      width="100%"
                      height={40}
                    />
                  ),
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="buttonMedium">{event.modelId}</Typography>
            <Typography variant="body2Regular" color="text.secondary">
              {formatTimestamp(event.timestamp)}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      title: "Feedback",
      content: event => {
        return (
          <FeedbackThumbsTableCellContainer
            event={event}
            onUpdateEventFeedback={updateEventFeedback}
          />
        );
      },
    },
    {
      title: "Feedback details",
      content: event => {
        return (
          <FeedbackDetailsTableCellContainer
            event={event}
            onUpdateEventFeedback={updateEventFeedback}
          />
        );
      },
    },
    {
      title: "Location",
      content: event => {
        const locationName = getCachedLocationName(event.location);

        return (
          <TableCellTruncated
            tooltipText={locationName}
            text={locationName}
            TypographyProps={{ variant: "body2" }}
          />
        );
      },
    },
  ];

  return { columns };
};
